import React, { useMemo, useState } from "react";

import BlockRenderer from "./BlockRenderer";

const PlaceholderCodeBlock = ({ config, onComplete }) => {
    const {
        template = "",
        language = "",
        options = [],
        placeholders = [],
        prompt = {},
        explanation = null,
        showCompiler = false,
    } = config;

    const [answers, setAnswers] = useState({});
    const [checked, setChecked] = useState(false);

    const isComplete = useMemo(() => {
        return placeholders.every(
            (placeholder) =>
                answers[placeholder.key] === placeholder.correctOptionText
        );
    }, [answers, placeholders]);

    const handleSelect = (key, value) => {
        if (checked) return;

        setAnswers((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleCheck = () => {
        setChecked(true);

        if (isComplete) {
            onComplete?.();
        }
    };

    const renderTemplate = () => {
        const parts = template.split(/(\[\[placeholder\d+\]\])/g);

        return parts.map((part, index) => {
            const match = part.match(/^\[\[placeholder(\d+)\]\]$/);

            if (!match) {
                return (
                    <span key={index}>
                        {part}
                    </span>
                );
            }

            const placeholderNumber = match[1];

            const placeholder = placeholders.find(
                (item) =>
                    item.key === `PLACEHOLDER${placeholderNumber}`
            );

            if (!placeholder) {
                return (
                    <span key={index}>
                        {part}
                    </span>
                );
            }

            const selected = answers[placeholder.key];

            const correct =
                checked &&
                selected === placeholder.correctOptionText;

            const incorrect =
                checked &&
                selected &&
                selected !== placeholder.correctOptionText;

            return (
                <span
                    key={index}
                    className={`
                        inline-flex
                        min-w-[80px]
                        mx-1
                        px-2
                        py-1
                        rounded
                        border
                        font-semibold
                        ${
                        correct
                            ? "border-green-500 bg-green-100 text-green-700"
                            : incorrect
                                ? "border-red-500 bg-red-100 text-red-700"
                                : "border-gray-400 bg-gray-100"
                    }
                    `}
                >
                    {selected || `___`}
                </span>
            );
        });
    };

    return (
        <div className="w-full">

            {/* Prompt */}
            {prompt?.blocks?.length > 0 && (
                <div className="mb-6">
                    {prompt.blocks.map((block, index) => (
                        <BlockRenderer
                            key={block.id || index}
                            block={block}
                        />
                    ))}
                </div>
            )}

            {/* Code */}
            <div
                className="
                    bg-gray-900
                    text-white
                    rounded-lg
                    p-6
                    overflow-x-auto
                    font-mono
                    text-sm
                    leading-8
                    whitespace-pre-wrap
                "
            >
                {renderTemplate()}
            </div>

            {/* Options */}
            <div className="flex flex-wrap gap-3 mt-6">
                {options.map((option, index) => {
                    const value =
                        typeof option === "string"
                            ? option
                            : option.text ?? option.value;

                    const selected = Object.values(answers).includes(value);

                    return (
                        <button
                            key={option.id || index}
                            type="button"
                            disabled={checked || selected}
                            onClick={() => {
                                const emptyPlaceholder = placeholders.find(
                                    (placeholder) =>
                                        !answers[placeholder.key]
                                );

                                if (emptyPlaceholder) {
                                    handleSelect(
                                        emptyPlaceholder.key,
                                        value
                                    );
                                }
                            }}
                            className={`
                                px-4
                                py-2
                                rounded-lg
                                border
                                font-medium
                                transition
                                ${
                                selected
                                    ? "bg-gray-300 border-gray-400"
                                    : "bg-white border-gray-300 hover:bg-gray-100"
                            }
                                ${
                                checked
                                    ? "cursor-default"
                                    : "cursor-pointer"
                            }
                            `}
                        >
                            {value}
                        </button>
                    );
                })}
            </div>

            {/* Check */}
            {!checked && (
                <button
                    type="button"
                    onClick={handleCheck}
                    disabled={
                        placeholders.some(
                            (placeholder) =>
                                !answers[placeholder.key]
                        )
                    }
                    className="
                        mt-6
                        px-6
                        py-3
                        rounded-lg
                        bg-blue-600
                        text-white
                        font-bold
                        disabled:opacity-50
                        disabled:cursor-not-allowed
                    "
                >
                    بررسی
                </button>
            )}

            {/* Result */}
            {checked && (
                <div
                    className={`
                        mt-6
                        p-4
                        rounded-lg
                        ${
                        isComplete
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                    }
                    `}
                >
                    {isComplete
                        ? "پاسخ شما درست است."
                        : "بعضی از پاسخ‌ها اشتباه هستند."}
                </div>
            )}

            {/* Explanation */}
            {checked && explanation?.blocks?.length > 0 && (
                <div className="mt-6">
                    {explanation.blocks.map((block, index) => (
                        <BlockRenderer
                            key={block.id || index}
                            block={block}
                        />
                    ))}
                </div>
            )}

            {/* Compiler */}
            {showCompiler && (
                <div className="mt-6">
                    {/* Compiler component goes here */}
                </div>
            )}
        </div>
    );
};

export default PlaceholderCodeBlock;